import * as React from 'react'
import { cn } from '@/lib/utils'
import { Icon } from '@/design-system/atoms/Icon'

export interface CheckboxProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'size'
> {
  label?: React.ReactNode
  size?: 'sm' | 'md' | 'lg'
}

const sizeMap = {
  sm: { box: 'h-4 w-4', icon: 12, text: 'text-xs' },
  md: { box: 'h-5 w-5', icon: 14, text: 'text-sm' },
  lg: { box: 'h-6 w-6', icon: 16, text: 'text-base' },
} as const

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, label, size = 'md', disabled, id, ...rest }, ref) => {
    const sizing = sizeMap[size]
    const autoId = React.useId()
    const inputId = id ?? autoId
    return (
      <label
        htmlFor={inputId}
        className={cn(
          'inline-flex items-center gap-2',
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
        )}
      >
        <span className="relative inline-flex">
          <input
            ref={ref}
            type="checkbox"
            id={inputId}
            disabled={disabled}
            className={cn(
              'peer appearance-none rounded-sm border border-gray-300 bg-white transition-colors checked:border-primary-500 checked:bg-primary-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:ring-offset-1 disabled:cursor-not-allowed dark:border-gray-600 dark:bg-gray-800 dark:checked:bg-primary-500',
              sizing.box,
              className
            )}
            {...rest}
          />
          <Icon
            name="check"
            size={sizing.icon}
            strokeWidth={2}
            className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white opacity-0 transition-opacity peer-checked:opacity-100"
          />
        </span>
        {label && (
          <span
            className={cn(
              'select-none text-gray-800 dark:text-gray-200',
              sizing.text
            )}
          >
            {label}
          </span>
        )}
      </label>
    )
  }
)

Checkbox.displayName = 'Checkbox'
